import { Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { AppShell } from "@/shell/AppShell";
import { Login } from "@/shell/Login";
import { NoAccess } from "@/shell/NoAccess";
import { SessionGate } from "@/shell/SessionGate";
import { LEGACY_REDIRECTS, firstPathOf, modules } from "@/modules";
import { DataTableSkeleton } from "@/components/data-table";

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/no-access" element={<NoAccess />} />
      <Route
        path="/*"
        element={
          <SessionGate>
            <AppShell>
              <Suspense fallback={<PageLoading />}>
                <Routes>
                  <Route index element={<Navigate to="/overview" replace />} />

                  {/* Paths the portal used to serve. The regroup moved four
                      modules and merged two; these keep every bookmark, Slack
                      link and runbook step resolving instead of 404-ing with no
                      explanation. */}
                  {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                    <Route
                      key={`legacy-${from}`}
                      path={from.replace(/^\//, "")}
                      element={<Navigate to={to} replace />}
                    />
                  ))}

                  {modules.flatMap((m) => {
                    const first = firstPathOf(m);
                    const nodes = m.routes.map((r) => (
                      <Route
                        key={`${m.key}-${r.path || "index"}`}
                        path={r.path ? `${m.key}/${r.path}` : m.key}
                        element={r.element}
                      />
                    ));
                    // Add a /{key} index that redirects to the module's first
                    // sub-route — unless the module already declared an
                    // empty-path route (i.e. it has a natural landing page).
                    if (m.routes.some((r) => r.path === "")) return nodes;
                    return [
                      ...nodes,
                      <Route
                        key={`${m.key}-redirect`}
                        path={m.key}
                        element={<Navigate to={`/${m.key}/${first}`} replace />}
                      />,
                    ];
                  })}

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AppShell>
          </SessionGate>
        }
      />
    </Routes>
  );
}

function PageLoading() {
  return (
    <div className="container mx-auto space-y-5 px-4 py-4 md:px-6 md:py-6">
      <div className="h-7 w-40 animate-pulse rounded-md bg-muted" />
      <DataTableSkeleton />
    </div>
  );
}

function NotFound() {
  return (
    <div className="container mx-auto px-4 py-10 md:px-6">
      <h1 className="text-xl font-semibold">Not found</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        That path does not exist in the admin portal.
      </p>
    </div>
  );
}
